// Customer logo catalog for animated grid rotation — ids, links, slot layouts.

import { CUSTOMER_GRID_BRANDS } from "./customers-grid-data";
import type { CustomerGridBrand } from "./customers-grid-data";

export type CustomerLogoId = string;

export interface CustomerLogoEntry {
  id: CustomerLogoId;
  name: string;
  logo: NonNullable<CustomerGridBrand["logo"]>;
  href: string;
  desktopMediaSize: string;
  compactMediaSize: string;
}

export interface LogoSlotConfig {
  id: CustomerLogoId;
  plusBg?: string;
}

const MEDIA_SIZE = "[&_img]:max-w-[min(117px,100%)] [&_img]:object-contain";
const STANDARD_DESKTOP_HEIGHT = "[&_img]:max-h-[34px]";
const DETAILED_DESKTOP_HEIGHT = "[&_img]:max-h-[40px]";
const COMPACT_MEDIA_SIZE =
  "[&_img]:max-h-9 [&_img]:max-w-[min(92px,100%)] [&_img]:object-contain";
const LIGHT_LOGO_TREATMENT = "[&_img]:[filter:brightness(0)_opacity(0.42)]";
// Solid-colour logos (e.g. deep red): flatten to black first so they land on
// exactly the same grey as the black logos below.
const FLAT_LOGO_TREATMENT =
  "[&_img]:mix-blend-multiply [&_img]:[filter:brightness(0)_opacity(0.55)]";
const DARK_LOGO_TREATMENT =
  "[&_img]:mix-blend-multiply [&_img]:[filter:grayscale(1)_opacity(0.55)]";

function logoTreatmentClass(
  treatment: NonNullable<CustomerGridBrand["logo"]>["treatment"]
): string {
  if (treatment === "light") return LIGHT_LOGO_TREATMENT;
  if (treatment === "flat") return FLAT_LOGO_TREATMENT;
  return DARK_LOGO_TREATMENT;
}

export const CUSTOMER_LOGO_CATALOG = Object.fromEntries(
  CUSTOMER_GRID_BRANDS.flatMap((brand) =>
    brand.logo
      ? [
          [
            brand.id,
            {
              id: brand.id,
              name: brand.name,
              logo: brand.logo,
              href: brand.href,
              desktopMediaSize: `${MEDIA_SIZE} ${
                brand.logo.displaySize === "detailed"
                  ? DETAILED_DESKTOP_HEIGHT
                  : STANDARD_DESKTOP_HEIGHT
              } ${logoTreatmentClass(brand.logo.treatment)}`,
              compactMediaSize: `${COMPACT_MEDIA_SIZE} ${logoTreatmentClass(
                brand.logo.treatment
              )}`,
            } satisfies CustomerLogoEntry,
          ],
        ]
      : []
  )
) as Record<CustomerLogoId, CustomerLogoEntry>;

export function getCustomerLogoEntry(id: CustomerLogoId): CustomerLogoEntry {
  const entry = CUSTOMER_LOGO_CATALOG[id];
  if (!entry) {
    throw new Error(`Unknown customer logo: ${id}`);
  }
  return entry;
}

export function logoConfigsToSlots(
  configs: { id: CustomerLogoId; shortCard?: unknown }[]
): LogoSlotConfig[] {
  return configs.map(({ id, shortCard }) => ({
    id,
    plusBg: shortCard ? "bg-grey-50" : undefined,
  }));
}
